import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SeoService } from '../../core/seo/seo.service';
import { combineLatest, switchMap } from 'rxjs';
import { PropertyDataProvider } from '../../core/data/property-data.provider';
import { RouterLink } from '@angular/router';
import { CityDetail, Granularity } from '../../core/models/domain.models';
import { NamedSeries, TrendChartComponent } from '../../shared/charts.components';
import { Bg1Pipe, Bg2Pipe, BgIntPipe, EurM2Pipe, PctPipe } from '../../shared/format.pipes';
import { GranularityToggleComponent, MarketNoticeComponent, PageHeaderComponent } from '../../shared/ui.components';

@Component({
  selector: 'app-compare-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    PageHeaderComponent,
    MarketNoticeComponent,
    GranularityToggleComponent,
    TrendChartComponent,
    EurM2Pipe,
    BgIntPipe,
    Bg1Pipe,
    Bg2Pipe,
    PctPipe,
  ],
  templateUrl: './compare.page.html',
  styleUrl: './compare.page.scss',
})
export class ComparePage {
  private readonly data = inject(PropertyDataProvider);

  readonly granularity = signal<Granularity>('month');
  readonly selected = signal<string[]>(['sofia', 'varna']);

  readonly meta = toSignal(this.data.meta());
  readonly details = toSignal(
    combineLatest([toObservable(this.selected), toObservable(this.granularity)]).pipe(
      switchMap(([slugs, g]) => this.data.compare(slugs, g)),
    ),
  );

  readonly priceSeries = computed<NamedSeries[]>(
    () => (this.details() ?? []).map((d) => ({ name: d.city.name, series: d.price.series })),
  );
  readonly rentSeries = computed<NamedSeries[]>(
    () => (this.details() ?? []).map((d) => ({ name: d.city.name, series: d.rent.series })),
  );
  readonly affordabilitySeries = computed<NamedSeries[]>(
    () => (this.details() ?? []).map((d) => ({ name: d.city.name, series: d.affordability.series })),
  );
  readonly cutSeries = computed<NamedSeries[]>(
    () => (this.details() ?? []).map((d) => ({ name: d.city.name, series: d.priceCutRate.series })),
  );

  constructor(seo: SeoService) {
    seo.set({
      title: 'Сравнение на цени на имоти по градове 2026 — Имотомер',
      description: 'Сравнете до 4 български града по цени на кв.м, наеми, брутна доходност, достъпност и индекс на прегряване.',
      path: '/sravnenie',
      keywords: [
        'сравнение на имоти',
        'сравнение на градове цени',
        'цени на имоти софия пловдив варна',
        'доходност от наем градове',
        'имотомер',
        'imotomer',
      ],
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Начало', item: 'https://real-estate.tur92.com/' },
          { '@type': 'ListItem', position: 2, name: 'Сравнение на градове', item: 'https://real-estate.tur92.com/sravnenie' },
        ],
      },
    });
  }

  isSelected(slug: string): boolean {
    return this.selected().includes(slug);
  }

  toggleCity(slug: string): void {
    this.selected.update((cur) => {
      if (cur.includes(slug)) return cur.filter((s) => s !== slug);
      return cur.length >= 4 ? cur : [...cur, slug];
    });
  }

  maxYield(list: CityDetail[]): number | null {
    const vals = list.map((d) => d.rentalYield.headline).filter((v): v is number => v !== null && isFinite(v));
    return vals.length ? Math.max(...vals) : null;
  }

  minPrice(list: CityDetail[]): number | null {
    const vals = list.map((d) => d.price.headline).filter((v): v is number => v !== null && isFinite(v));
    return vals.length ? Math.min(...vals) : null;
  }

  maxGrowth(list: CityDetail[]): number | null {
    const vals = list.map((d) => d.price.yoyPct).filter((v): v is number => v !== null && isFinite(v));
    return vals.length ? Math.max(...vals) : null;
  }
}
